/**
 * v3.15-1162 — How It Works (story format, not workflow).
 *
 * No timelines. No numbered steps. No arrows. No boxes.
 * Each section is a single powerful statement that answers
 * "What happens after a partner sends us a client?"
 */
import PublicLayout from '../components/PublicLayout';
import EndPageCTA from '../components/EndPageCTA';
import {
  HubCommunicationsScreenshot,
  HubClientsScreenshot,
  HubDashboardScreenshot,
} from '../components/HubProductImagery';

const HowItWorksPage = () => (
  <PublicLayout testId="how-it-works-page">

    {/* HERO — full-viewport dark, massive serif */}
    <section className="bg-primary text-white px-6 py-32 lg:py-44">
      <div className="max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-8">The Partner Experience</p>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-8">
          What happens after<br />
          you send us a client?
        </h1>
        <p className="text-xl sm:text-2xl text-slate-300 font-light max-w-2xl leading-relaxed">
          This is the part you don&rsquo;t see — and the part you never have to think about again.
        </p>
      </div>
    </section>

    {/* 1 — Concierge takes over */}
    <section className="bg-white px-6 py-28 lg:py-36">
      <div className="max-w-5xl mx-auto">
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.1] max-w-3xl">
          Within minutes, your dedicated Concierge takes over.
        </h2>
        <p className="mt-8 text-lg text-slate-600 max-w-2xl leading-relaxed">
          One human owns your file from this moment forward. By name. Direct line. No call centers, no transfers, no department shuffle.
        </p>
      </div>
    </section>

    {/* 2 — Client talks to one person (with Communications screenshot, dark) */}
    <section className="bg-slate-900 text-white px-6 py-28 lg:py-36">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1]">
            Your client only ever talks to one person.
          </h2>
          <p className="mt-8 text-lg text-slate-300 max-w-md leading-relaxed">
            By name. Every call. Every text. Every update. From the first conversation through funding day.
          </p>
        </div>
        <HubCommunicationsScreenshot />
      </div>
    </section>

    {/* 3 — Paperwork is handled */}
    <section className="bg-white px-6 py-28 lg:py-36">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-16 items-center">
        <HubClientsScreenshot />
        <div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.1]">
            You stop chasing paperwork.
          </h2>
          <p className="mt-8 text-lg text-slate-600 max-w-md leading-relaxed">
            Pay stubs. Bank statements. ID verification. We request it, chase it, validate it — so your team can stay focused on the next client.
          </p>
        </div>
      </div>
    </section>

    {/* 4 — Live transparency, dark with gold accent */}
    <section className="bg-primary text-white px-6 py-28 lg:py-36">
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">Total Transparency</p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1]">
            You watch everything happen, live.
          </h2>
          <p className="mt-8 text-lg text-slate-300 max-w-xl mx-auto leading-relaxed">
            Every approval. Every lender response. Every status change. The Hub shows you exactly where the file stands — the moment it changes.
          </p>
        </div>
        <HubDashboardScreenshot />
      </div>
    </section>

    {/* 5 — Lenders & funding handled (with proof of same-day capability) */}
    <section className="bg-white px-6 py-28 lg:py-36">
      <div className="max-w-5xl mx-auto">
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.1] max-w-4xl">
          We coordinate the lenders.<br />
          We coordinate the funding.
        </h2>
        <p className="mt-8 text-lg text-slate-600 max-w-2xl leading-relaxed">
          Files are worked across nearly 150 lending partners — consumer, business, and specialty programs — until the right answer surfaces.
        </p>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl">
          <div>
            <p className="font-heading text-3xl font-bold text-gradient-gold leading-none">~150</p>
            <p className="mt-2 text-sm text-slate-600">Active lending partners across all programs</p>
          </div>
          <div>
            <p className="font-heading text-3xl font-bold text-gradient-gold leading-none">ACH</p>
            <p className="mt-2 text-sm text-slate-600">Standard disbursement with full audit trail in the Hub</p>
          </div>
          <div>
            <p className="font-heading text-3xl font-bold text-gradient-gold leading-none">Same-day</p>
            <p className="mt-2 text-sm text-slate-600">Wire capability on qualified files, where available</p>
          </div>
        </div>
      </div>
    </section>

    {/* 6 — CreditForward360, manifesto + proof */}
    <section className="bg-gradient-to-b from-slate-900 to-primary text-white px-6 py-32 lg:py-44">
      <div className="max-w-4xl mx-auto text-center">
        <p className="font-heading text-2xl sm:text-3xl text-gold italic font-medium mb-6 leading-relaxed">
          If today&rsquo;s answer is &ldquo;not yet&rdquo;...
        </p>
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-8">
          CreditForward360 prepares<br />tomorrow&rsquo;s approval.
        </h2>
        <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed mb-10">
          The client moves into a structured credit improvement program — diagnostic review, targeted plan, monitored progress. Once thresholds are met, the file is routed back to Consumer or Business Funding automatically.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-3xl mx-auto text-left">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-2">Step 1</p>
            <p className="text-base text-white font-medium">Diagnostic review of the client&rsquo;s current profile.</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-2">Step 2</p>
            <p className="text-base text-white font-medium">Tailored credit improvement roadmap, monitored monthly.</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-2">Step 3</p>
            <p className="text-base text-white font-medium">Automatic re-entry into the funding workflow when ready.</p>
          </div>
        </div>
      </div>
    </section>

    {/* 7 — Closing manifesto */}
    <section className="bg-white px-6 py-28 lg:py-36">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-primary leading-[1.05]">
          Your job ends<br />at the introduction.
        </h2>
        <p className="mt-8 font-heading text-3xl sm:text-4xl text-gradient-gold font-bold">
          Ours begins.
        </p>
      </div>
    </section>

    <EndPageCTA testId="how-end-cta" />
  </PublicLayout>
);

export default HowItWorksPage;
